<template lang="pug">
  DefaultLayout
    CommonSection(title="Выплаты")
      .b-affiliate-payouts-page
        ._tabs
          AffiliateTabs
        FormBlock._main
          BaseTable._table(:headers="$options.headers" :items="payouts" v-slot="{ items, headers, sort }")
            ._row(v-for="item in items" :key="item.id")
              ._cell {{ item.id }}
                BaseTableHeaderItem._header(v-bind="headers[0]" @sort="sort")
              ._cell {{ formatDate(item.createdAt) }}
                BaseTableHeaderItem._header(v-bind="headers[1]" @sort="sort")
              ._cell
                div(v-if="item.targetUser.id === user.id") На свой аккаунт
                UserTile(v-else :user="item.targetUser" :size="30" :fz="12")
                BaseTableHeaderItem._header(v-bind="headers[2]" @sort="sort")
              ._cell {{ item.amount / 100 }} ₽
                BaseTableHeaderItem._header(v-bind="headers[3]" @sort="sort")
              ._cell.-status Выполнен
                BaseTableHeaderItem._header(v-bind="headers[4]" @sort="sort")
        FormBlock._aside
          ._aside-header Доступно
          ._aside-value {{ balance }} ₽
          ._aside-form
            ._aside-subtitle Вывести средства
            BaseSelect(:options="$options.targetOptions" v-model="payoutType" label="Куда выводить" :hideNotSelected="true")
            BaseSelect(v-if="payoutType === 'referral-account'" :options="referralsOptions" v-model="targetReferral" label="Выберите реферала" :hideNotSelected="true")
            BaseInput(label="Введите сумму" v-model="payoutSum" iconName="rub")
            Btn._aside-btn(@click="createPayout") Вывести
</template>

<script>
import utils from '@/utils/common';
import api from '@/api/payout';
import userModel from '@/model/user';

import DefaultLayout from '@/layouts/Default.vue';
import CommonSection from '@/components/CommonSection.vue';
import BaseTable from '@/components/base/BaseTable.vue';
import BaseTableHeaderItem from '@/components/base/BaseTableHeaderItem.vue';
import BaseSelect from '@/components/BaseSelect.vue';
import FormBlock from '@/components/FormBlock.vue';
import UserTile from '@/components/UserTile.vue';
import BaseInput from '@/components/BaseInput.vue';
import AffiliateTabs from '@/components/affiliate/AffiliateTabs.vue';

export default {
  name: 'PersonalAffiliateReferralsPage',
  components: {
    AffiliateTabs,
    BaseInput,
    UserTile,
    FormBlock,
    DefaultLayout,
    CommonSection,
    BaseTable,
    BaseTableHeaderItem,
    BaseSelect,
  },
  headers: [
    { title: '№ платежа' },
    { title: 'Дата', sortFn: (a, b) => a.createdAt - b.createdAt },
    { title: 'Тип' },
    { title: 'Сумма', sortFn: (a, b) => a.amount - b.amount },
    { title: 'Статус' },
  ],
  targetOptions: [
    { value: 'own-account',      label: 'На свой аккаунт'      }, // eslint-disable-line
    { value: 'referral-account', label: 'На аккаунт реферала'  }, // eslint-disable-line
  ],
  data() {
    return {
      payoutType: '',
      targetReferral: '',
      payoutSum: '',
    };
  },
  created() {
    this.$store.dispatch('affiliate/fetch');
    this.$store.dispatch('payout/fetch');
  },
  computed: {
    user() { return this.$store.state.auth.user; },
    balance() { return this.user.affiliateBalance / 100; },
    referrals() { return this.$store.getters['affiliate/referrals']; },
    referralsOptions() { return this.referrals.map(r => ({ value: r['@id'], user: r })); },
    payouts() {
      return (this.$store.state.payout.items ?? []).map(p => ({ ...p, createdAt: new Date(p.createdAt) }));
    },
  },
  methods: {
    formatDate(date) {
      return utils.convertDateToString(date);
    },
    async createPayout() {
      let targetUser;
      let targetUserStr;

      switch (this.payoutType) {
        case 'own-account':
          targetUser = this.user['@id'];
          targetUserStr = 'на Ваш счет';
          break;
        case 'referral-account':
          targetUser = this.targetReferral;
          if (!targetUser) {
            this.$alert('Выберите реферала');
            return;
          }

          const referral = this.referrals.find(r => r['@id'] === targetUser); // eslint-disable-line no-case-declarations
          targetUserStr = `на счет реферала ${referral.name} ${referral.surname}`;
          break;
        default:
          this.$alert('Выберите кому выводить средства');
          return;
      }

      const amount = parseInt(this.payoutSum, 10);

      if (`${amount}` !== this.payoutSum || amount <= 0) {
        this.$alert('Сумма указана некорректно');
        return;
      }

      const question = `Вы действительно хотите перечислить ${amount} руб. с партнёрского баланса ${targetUserStr}`;
      if (!await this.$confirm(question)) {
        return;
      }

      api.create({ targetUser, amount: amount * 100 }, true)
        .catch(e => {
          this.$alert(
            e['hydra:description'] === 'Affiliate balance is low'
              ? 'Недостаточно средств'
              : 'Неизвестная ошибка. Обратитесь в техподдержку',
          );
        })
        .finally(() => {
          this.$store.dispatch('payout/fetch');
          userModel.fetchSelf();
        });
    },
  },
};
</script>

<style scoped lang="scss">
  .affiliate-payouts-page {
    display: grid;
    grid-template-columns: 1fr 312px;
    gap: 35px;
    align-items: start;

    &__tabs {
      grid-column: 1 / 2 span;
    }

    &__row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__cell {
      &--bold {
        font-weight: 500;
      }

      &--status {
        font-weight: 500;
        font-size: 12px;
        line-height: 15px;
        letter-spacing: 0.01em;
        color: #5AC169;
      }

      &.blocked { color: #F17275; }
      &.active { color: #5AC169; }
      &.not-confirmed { color: #FED101; }
    }

    ::v-deep .cell, &__cell {
      width: 18%;
      display: flex;
      text-align: center;
      justify-content: center;

      &:nth-child(1) {
        text-align: left;
        justify-content: flex-start;
      }

      &:nth-child(3) {
        width: 30%;
      }

      &:nth-child(5) {
        width: 16%;
      }
    }

    &__cell ::v-deep .name {
      font-size: 12px;
      color: #33A1FB;
    }

    &__header {
      display: none !important;
    }

    &__aside {
      &-header {
        font-weight: 500;
        font-size: 18px;
        line-height: 24px;
        color: #1E3147;
      }

      &-value {
        font-weight: 700;
        font-size: 30px;
        line-height: 38px;
        letter-spacing: 0.01em;
        color: #33A1FB;
        margin-bottom: 12px;
      }

      &-subtitle {
        margin-bottom: 2px;
        font-weight: 500;
        font-size: 14px;
        line-height: 18px;
        letter-spacing: 0.01em;
        color: #1E3147;
      }

      &-form {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      &-btn {
        margin-top: 16px;
      }
    }

    @media (max-width: 1250px) {
      grid-template-columns: 1fr;

      &__tabs {
        grid-column: 1 / 1 span;
        order: 1;
      }

      &__aside {
        order: 2;
      }

      &__main {
        order: 3;
      }
    }

    @media (max-width: 1000px) {
      &__main {
        background: transparent;
        padding: 0;
        box-shadow: none;
      }

      &__header {
        display: flex !important;
      }

      &__table ::v-deep .header {
        display: none;
      }

      &__table ::v-deep .body {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
      }

      &__row {
        &, &:nth-child(odd) {
          background: #FFFFFF;
          border-radius: 8px;
          padding: 12px 18px 22px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
      }

      ::v-deep .cell, &__cell {
        width: auto !important;
      }

      &__cell {
        display: flex;
        flex-direction: column-reverse;
        justify-self: flex-start;
        align-items: flex-start;
        gap: 5px;

        &:nth-child(3) {
          order: 1;
          grid-column: 1 / 2 span;
          padding-bottom: 20px;
          border-bottom: 1px solid #EBEFF2;
          justify-self: stretch;
          gap: 10px;
        }

        &:nth-child(1) { order: 2 }
        &:nth-child(2) { order: 3 }
        &:nth-child(4) { order: 4 }
        &:nth-child(5) { order: 5 }
      }
    }

    @media (max-width: 576px) {
      &__tabs {
        overflow-x: scroll;
      }

      &__table ::v-deep .body {
        grid-template-columns: 1fr;
      }
    }
  }
</style>
